import {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import { AuthContext } from "../../context/authContext";
import { ListaParticipantes } from "./ListaParticipantes";
import apiClient from "../../config/axiosConfig";

const MAX_LEN = 500;

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const truncate = (text, max = 40) =>
  text && text.length > max ? text.slice(0, max) + "…" : text;

export const PrivateChatWidget = ({
  sendPrivateMessage,
  conversations,
  activeMessages,
  unreadTotal,
  setActiveConversation,
  connected,
  deleteMessage,
  clearConversation,
}) => {
  const { authState } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [activeConversationId, setLocalActiveId] = useState(null);
  const [activeConversationNombre, setActiveConversationNombre] = useState("");
  const [localMessages, setLocalMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mostrarParticipantes, setMostrarParticipantes] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll automático al último mensaje
  useEffect(() => {
    if (open && activeConversationId) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [localMessages, open, activeConversationId]);

  // Sincronizar activeMessages del hook con localMessages (mensajes en tiempo real)
  useEffect(() => {
    if (!activeConversationId || activeMessages.length === 0) return;
    setLocalMessages((prev) => {
      const prevIds = new Set(prev.map((m) => m.id));
      const nuevos = activeMessages.filter((m) => !prevIds.has(m.id));
      if (nuevos.length === 0) return prev;
      return [...prev, ...nuevos];
    });
  }, [activeMessages, activeConversationId]);

  const handleOpen = () => {
    setOpen((v) => !v);
  };

  const handleSelectConversacion = useCallback(
    async (conv) => {
      setLocalActiveId(conv.otroUsuarioId);
      setActiveConversationNombre(conv.otroUsuarioNombre);
      setActiveConversation(conv.otroUsuarioId);
      setLocalMessages([]);
      setMostrarParticipantes(false);
      try {
        const res = await apiClient.get(
          `/chat-privado/historial/${conv.otroUsuarioId}`
        );
        setLocalMessages(res.data ?? []);
      } catch (err) {
        console.warn("Error cargando historial:", err);
      }
      setTimeout(() => inputRef.current?.focus(), 150);
    },
    [setActiveConversation]
  );

  const handleSelectParticipante = useCallback(
    async (participante) => {
      setLocalActiveId(participante.id);
      setActiveConversationNombre(participante.nombre);
      setActiveConversation(participante.id);
      setLocalMessages([]);
      setMostrarParticipantes(false);
      try {
        const res = await apiClient.get(
          `/chat-privado/historial/${participante.id}`
        );
        setLocalMessages(res.data ?? []);
      } catch (err) {
        console.warn("Error cargando historial:", err);
      }
      setTimeout(() => inputRef.current?.focus(), 150);
    },
    [setActiveConversation]
  );

  const handleVolver = () => {
    setLocalActiveId(null);
    setActiveConversationNombre("");
    setLocalMessages([]);
    setInput("");
    setMostrarParticipantes(false);
  };

  const handleSend = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!input.trim() || !activeConversationId) return;
      await sendPrivateMessage(activeConversationId, input.trim());
      setInput("");
      inputRef.current?.focus();
    },
    [input, activeConversationId, sendPrivateMessage]
  );

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteMessage = useCallback(
    async (mensajeId) => {
      if (!window.confirm("¿Eliminar este mensaje?")) return;

      setIsDeleting(true);
      try {
        const result = await deleteMessage(mensajeId);
        if (result.exito) {
          setLocalMessages((prev) => prev.filter((m) => m.id !== mensajeId));
        } else {
          alert("No se pudo eliminar el mensaje");
        }
      } catch (err) {
        console.error("Error eliminando mensaje:", err);
        alert("Error al eliminar el mensaje");
      } finally {
        setIsDeleting(false);
        setHoveredMessageId(null);
      }
    },
    [deleteMessage]
  );

  const handleClearConversation = useCallback(async () => {
    if (!window.confirm("¿Eliminar todos los mensajes de esta conversación?"))
      return;

    setIsDeleting(true);
    try {
      const result = await clearConversation(activeConversationId);
      if (result.exito) {
        setLocalMessages([]);
      } else {
        alert("No se pudo limpiar la conversación");
      }
    } catch (err) {
      console.error("Error limpiando conversación:", err);
      alert("Error al limpiar la conversación");
    } finally {
      setIsDeleting(false);
    }
  }, [activeConversationId, clearConversation]);

  if (!authState.isLoggedIn) return null;

  const myId = String(authState.user);

  return (
    <div className="private-chat__widget">
      {/* Botón flotante (FAB) */}
      <button
        className={`private-chat__fab ${open ? "private-chat__fab--open" : ""}`}
        onClick={handleOpen}
        aria-label={open ? "Cerrar mensajes privados" : "Abrir mensajes privados"}
      >
        {open ? (
          <span style={{ fontSize: "1.2rem" }}>✕</span>
        ) : (
          <>
            <span style={{ fontSize: "1.4rem" }}>📩</span>
            {unreadTotal > 0 && (
              <span className="private-chat__fab-badge">
                {unreadTotal > 9 ? "9+" : unreadTotal}
              </span>
            )}
          </>
        )}
      </button>

      {/* Panel principal */}
      <div
        className={`private-chat__panel ${open ? "private-chat__panel--open" : ""}`}
        role="dialog"
        aria-label="Mensajes privados"
      >
        {/* ── Vista 2: Conversación activa ── */}
        {activeConversationId ? (
          <>
            {/* Header conversación */}
            <div className="private-chat__header">
              <button
                className="private-chat__back-btn"
                onClick={handleVolver}
                aria-label="Volver a la lista"
              >
                ←
              </button>
              <span className="private-chat__header-nombre">
                {activeConversationNombre}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  className="private-chat__clear-btn"
                  onClick={handleClearConversation}
                  disabled={isDeleting || localMessages.length === 0}
                  title="Limpiar conversación"
                  aria-label="Limpiar todos los mensajes"
                >
                  🗑️
                </button>
                <div
                  className={`private-chat__status ${
                    connected
                      ? "private-chat__status--on"
                      : "private-chat__status--off"
                  }`}
                >
                  <span className="private-chat__status-dot" />
                  {connected ? "Conectado" : "Reconectando..."}
                </div>
                <button
                  className="chat-close-btn"
                  onClick={handleOpen}
                  aria-label="Cerrar mensajes privados"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Lista de mensajes */}
            <div className="private-chat__body">
              {localMessages.length === 0 && (
                <div className="private-chat__empty">
                  <span style={{ fontSize: "1.8rem" }}>💬</span>
                  <p>Inicia la conversación</p>
                </div>
              )}
              {localMessages.map((msg, idx) => {
                const isMe =
                  msg._optimista ||
                  String(msg.emisorId) === myId;
                return (
                  <div
                    key={msg.id ?? idx}
                    className={`private-chat__msg ${
                      isMe
                        ? "private-chat__msg--me"
                        : "private-chat__msg--other"
                    }`}
                    onMouseEnter={() => isMe && setHoveredMessageId(msg.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {!isMe && (
                      <p className="private-chat__msg-author">
                        {msg.emisorNombre}
                      </p>
                    )}
                    <div className="private-chat__msg-bubble">
                      <span className="private-chat__msg-text">
                        {msg.contenido}
                      </span>
                      <span className="private-chat__msg-time">
                        {formatTime(msg.fechaEnvio)}
                      </span>
                    </div>
                    {isMe && hoveredMessageId === msg.id && !msg._optimista && (
                      <button
                        className="private-chat__msg-delete"
                        onClick={() => handleDeleteMessage(msg.id)}
                        disabled={isDeleting}
                        aria-label="Eliminar mensaje"
                        title="Eliminar mensaje"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <form className="private-chat__footer" onSubmit={handleSend}>
              <input
                ref={inputRef}
                className="private-chat__input"
                type="text"
                placeholder="Escribe un mensaje..."
                value={input}
                onChange={(e) =>
                  setInput(e.target.value.slice(0, MAX_LEN))
                }
                onKeyDown={handleKey}
                disabled={!connected}
                maxLength={MAX_LEN}
                autoComplete="off"
              />
              <button
                className="private-chat__send-btn"
                type="submit"
                disabled={!connected || !input.trim()}
                aria-label="Enviar mensaje"
              >
                ➤
              </button>
            </form>
          </>
        ) : (
          /* ── Vista 1: Lista de conversaciones ── */
          <>
            <div className="private-chat__header">
              <span style={{ fontSize: "1.1rem" }}>📩</span>
              <p className="private-chat__title">Mensajes Privados</p>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  className="private-chat__participantes-btn"
                  onClick={() => setMostrarParticipantes((v) => !v)}
                  aria-label="Ver participantes"
                  title="Ver participantes"
                >
                  👥
                </button>
                <button
                  className="chat-close-btn"
                  onClick={handleOpen}
                  aria-label="Cerrar mensajes privados"
                >
                  ✕
                </button>
              </div>
            </div>

            {mostrarParticipantes ? (
              <div className="private-chat__body">
                <ListaParticipantes
                  setActiveConversation={setActiveConversation}
                  onSelectParticipante={handleSelectParticipante}
                />
              </div>
            ) : (
              <div className="private-chat__body">
                {conversations.length === 0 && (
                  <div className="private-chat__empty">
                    <span style={{ fontSize: "1.8rem" }}>💬</span>
                    <p>No tienes conversaciones aún</p>
                    <p style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                      Usa 👥 para iniciar una
                    </p>
                  </div>
                )}
                {conversations.map((conv) => (
                  <button
                    key={conv.otroUsuarioId}
                    className="private-chat__conv-item"
                    onClick={() => handleSelectConversacion(conv)}
                  >
                    <div className="private-chat__conv-info">
                      <span className="private-chat__conv-nombre">
                        {conv.otroUsuarioNombre}
                      </span>
                      <span className="private-chat__conv-ultimo">
                        {truncate(conv.ultimoMensaje)}
                      </span>
                    </div>
                    {conv.noLeidos > 0 && (
                      <span className="private-chat__conv-badge">
                        {conv.noLeidos > 9 ? "9+" : conv.noLeidos}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
